import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../services/apiClient';
import { useSessionRealtime } from '../../context/SessionRealtimeContext';
import { usePlayerAssignment } from '../../hooks/usePlayerAssignment';
import { useAudio } from '../../hooks/useAudio';
import { IntroPage } from './IntroPage';
import { QuestionPage } from './QuestionPage';
import { WaitingPage } from './WaitingPage';
import { RoundCompletePage } from './RoundCompletePage';
import { UnlockPage } from './UnlockPage';
import { FinishedPage } from './FinishedPage';
import { StoppedPage } from './StoppedPage';

export function PlayerFlowPage() {
  const { sessionStatus, eventVersion } = useSessionRealtime();
  const { assignment, setAssignment, clearAssignment } = usePlayerAssignment();
  const [playerState, setPlayerState] = useState(null);
  const [name, setName] = useState(assignment?.name || '');
  const [joinError, setJoinError] = useState('');
  const [joining, setJoining] = useState(false);
  const [answer, setAnswer] = useState('');
  const [answerBool, setAnswerBool] = useState(null);
  const [questionError, setQuestionError] = useState('');
  const [secretWord, setSecretWord] = useState('');
  const [unlockError, setUnlockError] = useState('');
  const [showUnlock, setShowUnlock] = useState(false);
  const [lockRemaining, setLockRemaining] = useState(0);

  const playCorrect = useAudio(playerState?.assets?.correctSound?.url);
  const playWrong = useAudio(playerState?.assets?.wrongSound?.url);
  const playRoundComplete = useAudio(playerState?.assets?.roundCompleteSound?.url);

  const fetchPlayerState = useCallback(async () => {
    if (!assignment?.playerToken) {
      setPlayerState(null);
      return;
    }
    try {
      const nextState = await apiClient.getSessionMe();
      setPlayerState(nextState);
      if (!nextState.joined && sessionStatus?.status === 'idle') {
        clearAssignment();
      }
    } catch {
      clearAssignment();
      setPlayerState(null);
    }
  }, [assignment?.playerToken, clearAssignment, sessionStatus?.status]);

  useEffect(() => {
    fetchPlayerState();
  }, [eventVersion, fetchPlayerState]);

  useEffect(() => {
    if (playerState?.phase === 'roundComplete' && playerState?.lastCompletedRound?.hasNextRound) {
      const timer = setTimeout(() => setShowUnlock(true), 1500);
      return () => clearTimeout(timer);
    }
    setShowUnlock(false);
    return undefined;
  }, [playerState?.phase, playerState?.lastCompletedRound?.hasNextRound]);

  useEffect(() => {
    if (!playerState?.lockUntil) {
      setLockRemaining(0);
      return undefined;
    }

    const updateRemaining = () => {
      const nextRemaining = Math.max(0, Math.ceil((new Date(playerState.lockUntil).getTime() - Date.now()) / 1000));
      setLockRemaining(nextRemaining);
      return nextRemaining;
    };

    const initialRemaining = updateRemaining();
    if (initialRemaining === 0) {
      return undefined;
    }

    const timer = setInterval(() => {
      if (updateRemaining() === 0) {
        clearInterval(timer);
      }
    }, 250);

    return () => clearInterval(timer);
  }, [playerState?.lockUntil]);

  useEffect(() => {
    setAnswer('');
    setAnswerBool(null);
    setQuestionError('');
  }, [playerState?.currentQuestion?.id]);

  if (!sessionStatus) return null;

  const assignedLabel = assignment?.playerLabel || playerState?.assignedMessage || '';
  const header = {
    appName: 'Digital Escape Room',
    quizTitle: playerState?.quiz?.publicTitle || sessionStatus?.title,
    showLogo: (playerState?.quiz?.showLogo ?? sessionStatus?.showLogo) ?? true,
    logoUrl: playerState?.assets?.logo?.url || sessionStatus?.assets?.logo?.url
  };

  if (sessionStatus.status === 'stopped' || playerState?.phase === 'stopped') {
    return <StoppedPage header={header} reason={playerState?.stopReason || sessionStatus.stopReason || 'Igra je zaustavljena od strane administratora.'} />;
  }

  if (!assignment?.playerToken || playerState?.phase === 'intro' || sessionStatus.status === 'idle') {
    return (
      <IntroPage
        header={header}
        sessionStatus={sessionStatus}
        name={name}
        setName={setName}
        joining={joining}
        error={joinError}
        onJoin={async () => {
          try {
            setJoining(true);
            setJoinError('');
            const joinedPlayer = await apiClient.joinSession(name);
            setAssignment(joinedPlayer);
            setName(joinedPlayer.name);
          } catch (error) {
            setJoinError(error.message);
          } finally {
            setJoining(false);
          }
        }}
      />
    );
  }

  if (!playerState) return null;

  if (playerState.phase === 'question') {
    return (
      <QuestionPage
        playerState={playerState}
        header={header}
        answer={answer}
        setAnswer={setAnswer}
        answerBool={answerBool}
        setAnswerBool={setAnswerBool}
        error={questionError}
        assignedLabel={assignedLabel}
        lockRemaining={lockRemaining}
        onSubmit={async () => {
          try {
            setQuestionError('');
            const result = await apiClient.submitAnswer({
              questionId: playerState.currentQuestion.id,
              ...(playerState.currentQuestion.type === 'ABCD' ? { answer } : { answerBool })
            });
            if (!result.correct) {
              setQuestionError(result.message);
              if (result.playWrongSound) await playWrong();
              await fetchPlayerState();
              return;
            }
            if (result.playCorrectSound) await playCorrect();
            if (result.playRoundCompleteSound) await playRoundComplete();
            await fetchPlayerState();
          } catch (error) {
            setQuestionError(error.message);
          }
        }}
      />
    );
  }

  if (playerState.phase === 'waiting') {
    const waitingForPlayers = playerState.joinedCount < playerState.enabledPlayersCount;
    return (
      <WaitingPage
        header={header}
        assignedLabel={assignedLabel}
        title={waitingForPlayers ? 'Uspješno ste prijavljeni.' : undefined}
        message={waitingForPlayers ? 'Čekamo da se svi natjecatelji priključe pa igra počinje.' : undefined}
      />
    );
  }

  if (playerState.phase === 'roundComplete' && playerState.lastCompletedRound?.hasNextRound && !showUnlock) {
    return <RoundCompletePage header={header} assignedLabel={assignedLabel} completionMessage={playerState.lastCompletedRound.completionMessage} />;
  }

  if (playerState.phase === 'roundComplete' && playerState.lastCompletedRound?.hasNextRound) {
    return (
      <UnlockPage
        header={header}
        assignedLabel={assignedLabel}
        completionMessage={playerState.lastCompletedRound.completionMessage}
        secretWord={secretWord}
        setSecretWord={setSecretWord}
        error={unlockError}
        onUnlock={async () => {
          const result = await apiClient.unlockRound(secretWord);
          if (!result.ok) {
            setUnlockError(result.message);
            return;
          }
          setUnlockError('');
          setSecretWord('');
          await fetchPlayerState();
        }}
      />
    );
  }

  if (playerState.phase === 'finished') {
    return <FinishedPage header={header} assignedLabel={assignedLabel} completionMessage={playerState.lastCompletedRound?.completionMessage || 'Čestitamo!'} />;
  }

  return <WaitingPage header={header} assignedLabel={assignedLabel} />;
}
